// Typed blog content collection. The admin blog tools read/write against this
// shape, so keep field names stable when the CMS layer lands.

export type BlogSection = {
  heading: string;
  body: string[];
  bullets?: string[];
};

export type BlogPost = {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  category: BlogCategorySlug;
  excerpt: string;
  author: string;
  publishedAt: string; // ISO date
  updatedAt: string; // ISO date
  readMinutes: number;
  sections: BlogSection[];
  faqs: { q: string; a: string }[];
  related: string[]; // internal route paths
};

export type BlogCategorySlug =
  | "dscr-loans"
  | "investor-strategy"
  | "foreign-national"
  | "section-8"
  | "market-data";

export const BLOG_CATEGORIES: {
  slug: BlogCategorySlug;
  name: string;
  description: string;
}[] = [
  {
    slug: "dscr-loans",
    name: "DSCR loans",
    description:
      "How debt-service-coverage underwriting works, qualifying ratios, reserves, and closing timelines.",
  },
  {
    slug: "investor-strategy",
    name: "Investor strategy",
    description:
      "BRRRR, portfolio scaling, entity vesting, and exit planning for rental property investors.",
  },
  {
    slug: "foreign-national",
    name: "Foreign national",
    description:
      "Financing US investment property without US credit, an SSN, or US tax returns.",
  },
  {
    slug: "section-8",
    name: "Section 8 & HAP",
    description:
      "Housing choice voucher income, HUD fair market rents, and underwriting HAP contracts.",
  },
  {
    slug: "market-data",
    name: "Market data",
    description:
      "Rent benchmarks, HUD data releases, and local investor market fundamentals.",
  },
];

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "dscr-loan-requirements",
    title: "DSCR Loan Requirements for Rental Property Investors",
    metaTitle: "DSCR Loan Requirements 2026 | Simply Approved Mortgages",
    metaDescription:
      "DSCR loan requirements for investors: minimum ratio, credit score, down payment, reserves, entity vesting, and how rental income replaces tax returns.",
    keywords:
      "dscr loan requirements, dscr loan credit score, dscr minimum ratio, rental property loan no tax returns, investor loan reserves, dscr down payment",
    category: "dscr-loans",
    excerpt:
      "A DSCR loan qualifies on the property's rent, not your personal income. Here is exactly what underwriting looks at.",
    author: "Simply Approved Mortgages",
    publishedAt: "2026-01-14",
    updatedAt: "2026-08-10",
    readMinutes: 7,
    sections: [
      {
        heading: "What a DSCR loan actually measures",
        body: [
          "A debt-service-coverage-ratio loan compares the property's monthly rent to its monthly principal, interest, taxes, insurance, and association dues. If the rent covers the payment, the file works — regardless of what your personal tax returns say.",
          "That single change is why investors move to DSCR after their fourth or fifth property: conventional debt-to-income limits stop counting against you.",
        ],
        bullets: [
          "DSCR = gross monthly rent ÷ monthly PITIA",
          "A 1.00 ratio means rent exactly covers the payment",
          "Ratios below 1.00 are possible on select programs at reduced leverage",
        ],
      },
      {
        heading: "Credit, leverage, and reserves",
        body: [
          "Pricing improves in tiers. The strongest terms go to files with a higher score, a lower loan-to-value, and a ratio comfortably above break-even. Reserves are counted in months of PITIA and are typically held in a business or personal account you control.",
        ],
        bullets: [
          "Purchase and rate-term leverage up to 80% LTV, with 85% available only as a documented exception",
          "Cash-out leverage is set lower than purchase leverage on most programs",
          "Reserves commonly run 1–6 months of PITIA depending on loan size and ratio",
        ],
      },
      {
        heading: "Documents you will actually be asked for",
        body: [
          "Because there is no income calculation, the document list is short and property-focused. Most delays come from entity paperwork and insurance, not from the borrower.",
        ],
        bullets: [
          "Lease or market rent support for the subject property",
          "Two months of bank statements for down payment and reserves",
          "Entity documents when vesting in an LLC — articles, operating agreement, EIN",
          "Landlord insurance quote with the correct mortgagee clause",
        ],
      },
      {
        heading: "Where investors lose time",
        body: [
          "The two most common slowdowns are an insurance binder that arrives late and an operating agreement that does not name the signer. Sorting both in the first week usually keeps a purchase on a normal timeline.",
        ],
      },
    ],
    faqs: [
      {
        q: "What DSCR ratio do I need to qualify?",
        a: "Most programs treat 1.00 as the standard break-even. Lower ratios can work on select products at reduced leverage, and stronger ratios improve pricing.",
      },
      {
        q: "Do DSCR loans require tax returns?",
        a: "No. Qualification is based on the property's rental income versus its housing payment, not on personal income documentation.",
      },
      {
        q: "Can I close in an LLC?",
        a: "Yes. Business-purpose investor loans are routinely vested in an LLC, and a few states effectively require entity vesting.",
      },
    ],
    related: ["/dscr-loans", "/dscr-calculator", "/how-dscr-works"],
  },
  {
    slug: "brrrr-financing-playbook",
    title: "The BRRRR Financing Playbook: Bridge to DSCR Refinance",
    metaTitle: "BRRRR Financing Playbook: Bridge to DSCR Refi | Simply Approved",
    metaDescription:
      "How investors stack a bridge or hard money purchase with a DSCR refinance: seasoning, ARV, rehab draws, and timing the exit without stranding cash.",
    keywords:
      "brrrr financing, bridge to dscr refinance, rehab loan seasoning, arv refinance, hard money to dscr, cash out refinance rental",
    category: "investor-strategy",
    excerpt:
      "The rehab loan and the permanent loan are one plan. Sequencing them badly is what strands your capital.",
    author: "Simply Approved Mortgages",
    publishedAt: "2026-02-02",
    updatedAt: "2026-08-10",
    readMinutes: 8,
    sections: [
      {
        heading: "Underwrite the exit before the entry",
        body: [
          "BRRRR fails at the refinance, not the purchase. Before you sign on a bridge or hard money note, price the permanent loan against a conservative after-repair value and a conservative market rent.",
        ],
        bullets: [
          "Model the DSCR refinance at today's rate, not a hoped-for rate",
          "Use market rent support, not the highest comparable on the street",
          "Assume the appraiser lands under your ARV estimate",
        ],
      },
      {
        heading: "Seasoning and the value you can use",
        body: [
          "Seasoning rules decide whether the refinance uses your purchase price or the new appraised value. That single distinction can be the difference between recovering your rehab capital and leaving it in the deal.",
        ],
      },
      {
        heading: "Keeping the rehab loan clean",
        body: [
          "Draw schedules, permits, and contractor invoices become part of the refinance file. Keeping them organized during construction shortens the permanent loan by weeks.",
        ],
      },
    ],
    faqs: [
      {
        q: "How soon can I refinance out of a bridge loan?",
        a: "It depends on the permanent program's seasoning requirement and whether the property is leased. Plan the timeline before closing the bridge.",
      },
      {
        q: "Does the property need to be rented to refinance?",
        a: "A signed lease strengthens the file. Some programs allow market rent support when the unit is vacant but rent-ready.",
      },
    ],
    related: ["/bridge-loans", "/fix-and-flip-loans", "/brrrr-calculator"],
  },
  {
    slug: "foreign-national-investment-property-loans",
    title: "How Foreign Nationals Finance US Rental Property",
    metaTitle: "Foreign National Investment Property Loans | Simply Approved",
    metaDescription:
      "Financing US rental property without US credit or an SSN: entity setup, ITIN, banking, reserves, leverage limits, and the documents underwriting requires.",
    keywords:
      "foreign national loan, no us credit mortgage, itin investment property loan, non resident rental property financing, foreign investor us real estate",
    category: "foreign-national",
    excerpt:
      "No US credit score, no US tax return, no Social Security number — and still a fully financeable rental purchase.",
    author: "Simply Approved Mortgages",
    publishedAt: "2026-03-18",
    updatedAt: "2026-08-10",
    readMinutes: 6,
    sections: [
      {
        heading: "What replaces a US credit file",
        body: [
          "Foreign national programs substitute international credit references, verified reserves, and the property's own cash flow for a domestic credit score.",
        ],
        bullets: [
          "Valid passport and, where applicable, visa documentation",
          "Two international credit references or an international credit report",
          "Verified reserves held in a US or acceptable foreign institution",
        ],
      },
      {
        heading: "Leverage and pricing expectations",
        body: [
          "Leverage is set below the standard investor maximum, and pricing carries a premium over a comparable domestic DSCR file. Structuring the purchase in a US entity is the norm.",
        ],
      },
      {
        heading: "Setting up before you shop",
        body: [
          "The fastest closings belong to buyers who formed the LLC, opened the US business bank account, and seasoned reserves before going under contract.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do I need an ITIN to buy US investment property?",
        a: "An ITIN is not always required for the loan itself, but it is commonly needed for US tax filing on rental income.",
      },
      {
        q: "Can I buy in my own name?",
        a: "Most foreign national investor programs are structured through a US entity, which also simplifies liability and banking.",
      },
    ],
    related: [
      "/foreign-national-loans",
      "/business-setup/itin",
      "/business-setup/llc",
    ],
  },
  {
    slug: "section-8-rent-underwriting",
    title: "Underwriting Section 8 Rent: HAP vs. Full Contract Rent",
    metaTitle: "Section 8 DSCR Underwriting: HAP vs Contract Rent | Simply Approved",
    metaDescription:
      "How housing choice voucher income is underwritten: the HAP portion versus the tenant portion, HUD fair market rents, and modeling DSCR both ways.",
    keywords:
      "section 8 dscr loan, hap contract underwriting, housing choice voucher rent, hud fair market rent, voucher rental income, section 8 investment property",
    category: "section-8",
    excerpt:
      "The landlord receives the full contract rent — but only the HAP portion is government-paid. Model both.",
    author: "Simply Approved Mortgages",
    publishedAt: "2026-05-06",
    updatedAt: "2026-08-10",
    readMinutes: 6,
    sections: [
      {
        heading: "Two numbers, one lease",
        body: [
          "On a housing choice voucher lease the landlord collects 100% of the contract rent, but that rent is split: the housing authority pays the HAP portion directly, and the tenant pays the remainder.",
          "Underwrite conservatively on the HAP portion alone, then show the full-contract-rent case as the upside scenario.",
        ],
      },
      {
        heading: "Where HUD fair market rents fit",
        body: [
          "Published fair market rents set the ceiling a housing authority will approve in a given metro. They are a planning benchmark, not a guaranteed payment.",
        ],
      },
      {
        heading: "Inspections and timing",
        body: [
          "Housing quality standards inspections gate the first payment. Budget for the inspection cycle when projecting your first months of cash flow.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is voucher income guaranteed?",
        a: "The HAP portion is paid by the housing authority. The tenant portion is not government-guaranteed and should be underwritten as tenant-paid rent.",
      },
      {
        q: "Can Section 8 rent be used for DSCR qualification?",
        a: "Yes. The executed HAP contract and lease document the rent used in the ratio.",
      },
    ],
    related: [
      "/section-8-loans",
      "/hud-fair-market-rents",
      "/section-8-investing-map",
    ],
  },
];

export function getPost(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((p) => p.slug === slug);
}

export function getCategory(slug: string) {
  return BLOG_CATEGORIES.find((c) => c.slug === slug);
}

export function postsByCategory(slug: BlogCategorySlug): BlogPost[] {
  return sortedPosts().filter((p) => p.category === slug);
}

export function sortedPosts(): BlogPost[] {
  return [...BLOG_POSTS].sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
}

export function formatBlogDate(iso: string): string {
  const datePart = /^\d{4}-\d{2}-\d{2}/.exec(iso)?.[0];
  const date = new Date(datePart ? `${datePart}T00:00:00Z` : iso);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}
